import moment from 'moment';

/**
 * The donor reaction / incident report, translated between the three shapes it
 * exists in.
 *
 *   dialog   DonorReactionForm's own state - `date`, and the two vasodepressor
 *            treatment values nested under `treatmentVasodepressor`
 *   request  what PUT /processing/:id expects, mostly camelCase but with
 *            fluids_offered and treatment_other in snake_case
 *   row      donor_incident_reports as it comes back from the API, where the
 *            date column is `incident_date`
 *
 * Those three drifted apart, which is the whole reason incident details never
 * reached the database: the form wrote `date`, the worksheet initialised
 * `incidentDate`, and the payload that would have carried either was commented
 * out of the save.
 */

export const EMPTY_INCIDENT = {
    symptomsVasodepressor: [],
    treatmentVasodepressor: { fluidAmount: '', other: '' },
    symptomsCitrate: [],
    citrateTumsCount: '',
    symptomsOther: [],
    daeRequired: null,
    attendingPersonnel: '',
    date: '',
    comment: '',
    fileCopy: null,
    qaReview: '',
};

const asList = (value) => {
    if (Array.isArray(value)) {
        return value.filter(item => item !== null && item !== undefined && item !== '');
    }
    // MySQL JSON columns arrive as a string through some driver versions.
    if (typeof value === 'string' && value.trim()) {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }
    return [];
};

/** Tri-state: true, false, or "not answered". Never guess a default here. */
const asTriState = (value) => {
    if (typeof value === 'boolean') { return value; }
    if (value === 1 || value === '1') { return true; }
    if (value === 0 || value === '0') { return false; }
    return null;
};

const asText = value => (typeof value === 'string' ? value.trim() : (value === null || value === undefined ? '' : String(value).trim()));

/** '' rather than 0 for a blank number, so an unanswered field stays unanswered. */
const asNumberText = (value) => {
    if (value === null || value === undefined || value === '') { return ''; }
    const num = Number(value);
    return isNaN(num) ? '' : String(num);
};

const asNumberOrNull = (value) => {
    if (value === null || value === undefined || value === '') { return null; }
    const num = Number(value);
    return isNaN(num) ? null : num;
};

/**
 * Is there an incident here at all?
 *
 * Deliberately broader than "has a date". Someone can tick symptoms and write a
 * comment without filling the date in, and that is still an incident that must
 * be saved rather than silently discarded. The same test drives the button
 * label, the summary block and the save gate, so the three can never disagree
 * about whether a report exists.
 */
export const hasIncident = (incident) => {
    if (!incident) { return false; }

    const treatment = incident.treatmentVasodepressor || {};

    return !!(
        asText(incident.date) ||
        asText(incident.attendingPersonnel) ||
        asText(incident.comment) ||
        asText(incident.qaReview) ||
        asText(incident.citrateTumsCount) ||
        asText(treatment.fluidAmount) ||
        asText(treatment.other) ||
        incident.daeRequired === true || incident.daeRequired === false ||
        incident.fileCopy === true || incident.fileCopy === false ||
        asList(incident.symptomsVasodepressor).length ||
        asList(incident.symptomsCitrate).length ||
        asList(incident.symptomsOther).length
    );
};

/**
 * Dialog shape -> request body.
 *
 * Returns {} when there is no incident, so a worksheet without one adds
 * nothing to the payload and the server leaves the incident table alone. That
 * matters more than it looks: autosave sends this on every change, and an
 * empty object here is what stops it creating a blank incident row for every
 * donation anyone opens.
 */
export const incidentToApi = (incident) => {
    if (!hasIncident(incident)) { return {}; }

    const treatment = incident.treatmentVasodepressor || {};

    return {
        incdentId: incident.incdentId || null,
        incidentDate: asText(incident.date) || null,
        attendingPersonnel: asText(incident.attendingPersonnel),
        qaReview: asText(incident.qaReview),
        comment: asText(incident.comment),
        fileCopy: asTriState(incident.fileCopy),
        daeRequired: asTriState(incident.daeRequired),
        fluids_offered: asNumberOrNull(treatment.fluidAmount),
        treatment_other: asText(treatment.other),
        citrateTumsCount: asNumberOrNull(incident.citrateTumsCount),
        symptomsVasodepressor: asList(incident.symptomsVasodepressor),
        symptomsCitrate: asList(incident.symptomsCitrate),
        symptomsOther: asList(incident.symptomsOther),
    };
};

/** Stored row -> dialog shape. */
export const incidentFromApi = (row) => {
    if (!row || row.id === undefined || row.id === null) {
        return Object.assign({}, EMPTY_INCIDENT, {
            treatmentVasodepressor: { fluidAmount: '', other: '' },
        });
    }

    const rawDate = row.incident_date || '';

    return {
        incdentId: row.id,
        date: rawDate && moment(rawDate).isValid() ? moment(rawDate).format('YYYY-MM-DD') : '',
        attendingPersonnel: asText(row.attending_personnel),
        qaReview: asText(row.qa_review),
        comment: asText(row.comment),
        fileCopy: asTriState(row.file_copy),
        daeRequired: asTriState(row.dae_required),
        citrateTumsCount: asNumberText(row.citrate_tums_count),
        treatmentVasodepressor: {
            fluidAmount: asNumberText(row.fluids_offered),
            other: asText(row.treatment_other),
        },
        symptomsVasodepressor: asList(row.symptomsVasodepressor),
        symptomsCitrate: asList(row.symptomsCitrate),
        symptomsOther: asList(row.symptomsOther),
    };
};

export default incidentToApi;
